/** Pure logic that turns two successive trade snapshots into entry/exit
 * events. Kept framework-free so it is trivial to unit-test and so the
 * watcher hook (useTradeNotifications) stays thin.
 *
 * The watcher polls ALL of today's trades (not just open ones) so an exit
 * carries WHY it closed:
 *  - entry: a trade id present now (OPEN) that wasn't seen before.
 *  - exit:  a trade whose reason went OPEN -> terminal (SL/target/manual/...).
 *
 * The FIRST observation (prev === null) seeds the snapshot and emits NO
 * events, so loading the page with already-closed trades fires nothing. */

import type { ExitReason, TradeLogEntry } from "@/types/tradeLog";

export type TradeEventKind = "entry" | "exit";

export interface TradeEvent {
  kind: TradeEventKind;
  trade: TradeLogEntry;
}

export interface TradeEventDiff {
  events: TradeEvent[];
  /** Snapshot to carry into the next comparison (becomes the next `prev`). */
  next: Map<string, TradeLogEntry>;
}

export function computeTradeEvents(
  prev: Map<string, TradeLogEntry> | null,
  current: readonly TradeLogEntry[],
): TradeEventDiff {
  const next = new Map<string, TradeLogEntry>();
  for (const t of current) next.set(t.id, t);

  if (prev === null) return { events: [], next };

  const events: TradeEvent[] = [];
  for (const t of current) {
    const before = prev.get(t.id);
    if (before === undefined) {
      // Brand-new trade this poll — fire entry only if it's still open.
      if (t.exitReason === "OPEN") events.push({ kind: "entry", trade: t });
    } else if (before.exitReason === "OPEN" && t.exitReason !== "OPEN") {
      // Transitioned open -> closed; `t` carries the terminal reason.
      events.push({ kind: "exit", trade: t });
    }
  }
  return { events, next };
}

const EXIT_LABEL: Record<ExitReason, string> = {
  SL_HIT: "SL hit",
  LOSS_CAP: "loss cap",
  TARGET_HIT: "target hit",
  TRAIL_EXIT: "trail exit",
  FORCE_SQUAREOFF: "squared off (15:24)",
  MANUAL: "manual exit",
  EXIT_FAILED: "EXIT FAILED — position may still be OPEN",
  OPEN: "closed",
};

/** Notification title + body for one event. Emoji + contract up front so
 * the OS toast is scannable at a glance without opening the app. */
export function describeTradeEvent(event: TradeEvent): { title: string; body: string } {
  const t = event.trade;
  const symbol = t.contractSymbol ?? "—";
  // Paper fills alert exactly like live ones, but the toast must never
  // read as real money.
  const tag = t.executionMode === "PAPER" ? "[PAPER] " : "";
  if (event.kind === "entry") {
    const at = t.entryPremium !== null ? ` @ Rs ${t.entryPremium.toFixed(2)}` : "";
    return {
      title: `🟢 ${tag}Entry — ${symbol}`,
      body: `${t.strategyName} opened ${symbol}${at}`,
    };
  }
  return {
    title: `🔴 ${tag}Exit — ${symbol}`,
    body: `${t.strategyName}: ${EXIT_LABEL[t.exitReason]}`,
  };
}

/** Which of a strategy's sounds an event plays: its entry or exit sound, or its target sound when a
 * target closed the trade. */
export function soundEventOf(event: TradeEvent): "entry" | "exit" | "target" {
  if (event.kind === "entry") return "entry";
  return event.trade.exitReason === "TARGET_HIT" ? "target" : "exit";
}
